import React, { useState, useEffect, useCallback, useRef } from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'
import PropTypes from 'prop-types'
import updateRA from 'immutability-helper'
import moment from 'moment'
import Highcharts from 'highcharts'
import { Chart as HighchartsReact } from '@highcharts/react'
import {
  displayFormula,
  pressureFourToTwenty,
} from 'altrac-conversions'
import { DecimalInput, isNumber } from '../Inputs/Inputs'

import DateRangePicker from '../ConfigurePageComponents/DateRangePicker'
import { useDateRangePicker } from '../ConfigurePageComponents/useDateRangePicker'

import AppDispatcher from '../../../dispatchers/AppDispatcher'

import styles from '../../../../css/settings.module.css'
import stylesTele from '../../../../css/telemetry.module.css'

const DEFAULT_SETTINGS = {
  calibrate1: 1,
  calibrate2: 1,
}

function pressure (formula, multiplier, valueKey, reading0, physical) {
  if (reading0[valueKey] > 0) {
    return displayFormula(
      formula,
      multiplier,
      1,
      null,
      valueKey,
      reading0,
      null,
      physical
    )
  }

  return 0
}

function pressureToCurrent (valueKey, value, physical) {
  if (value > 0) {
    return pressureFourToTwenty(
      valueKey,
      value,
      physical
    )
  }
  return 0
}

function Graph (props) {
  const {
    deviceReading,
    calibrate1,
    calibrate2,
    formatMessage,
    physical,
    input,
  } = props

  const { deviceType } = physical

  const chartData = []

  let feedbackValue = 0
  let pressureValue = 0

  if (input === '1') {
    feedbackValue = Number(calibrate1)
  } else {
    feedbackValue = Number(calibrate2)
  }

  // Don't mutate props; Highcharts re-renders can get noisy if we do.
  const sortedReadings = (Array.isArray(deviceReading) && deviceReading.length > 0)
    ? [...deviceReading].sort((a, b) => {
      const dateA = new Date(a.date)
      const dateB = new Date(b.date)
      return dateA - dateB
    })
    : []

  sortedReadings.forEach((item) => {
    if ((deviceType === 'pump') && input === '1') {
      pressureValue = pressure('current4To20', 1, 'R1F', item, physical)
      chartData.push([moment(item.date).valueOf(), pressureValue])
    } else if ((deviceType === 'pump') && isNumber(item.R2F) && input === '2') {
      pressureValue = pressure('current4To20', 1, 'R2F', item, physical)
      chartData.push([moment(item.date).valueOf(), pressureValue])
    } else if (deviceType === 'valve' && isNumber(item['142']) && input === '1') {
      pressureValue = pressure('fourToTwentySD1', 10000, '142', item, physical)
      chartData.push([moment(item.date).valueOf(), pressureValue])
    } else if (deviceType === 'valve' && isNumber(item['143']) && input === '2') {
      pressureValue = pressure('fourToTwentySD2', 10000, '143', item, physical)
      chartData.push([moment(item.date).valueOf(), pressureValue])
    }
  })
  const series = [
    {
      turboThreshold: 6000,
      name: 'Feedback',
      data: chartData,
      type: 'spline',

    },
  ]
  const plotLines = [
    {
      label: {
        text: formatMessage({
          id: 'setting.pump.feedbackThreshold.value',
          defaultMessage: 'Feedback Threshold',
          description: 'Feedback Threshold label',
        }),
        align: 'right',
        y: -10,
      },
      value: feedbackValue,
      zIndex: 5,
      width: 2,
      color: '#ff0000',
    },
  ]

  return (

    <HighchartsReact
      highcharts={Highcharts}
      options={{
        accessibility: {
          enabled: false,
        },
        title: {
          text: '',
        },
        tooltip: {
          xDateFormat: '%b %e %H:%M',
          shared: true,
        },
        xAxis: {
          type: 'datetime',
          lineWidth: 0,
          minorGridLineWidth: 0,
          labels: {
            enabled: false,
          },
          minorTickLength: 0,
          tickLength: 0,
        },
        yAxis: [{ // Primary yAxis
          labels: {
            format: '{value}',
          },
          title: {
            text: '',
          },
          plotLines,

        }],
        series,
        colors: ['#23ce6b'],
        exporting: {
          enabled: false,
        },
        credits: {
          enabled: false,
        },
      }}
    />
  )
}

const USE_MOCK_DATA = false

const MOCK_DEVICE_READINGS = [
  { date: '2026-01-09T21:23:17.000Z', R1F: 0, R2F: 0 },
  { date: '2026-01-09T21:18:17.000Z', R1F: 0, R2F: 0 },
  { date: '2026-01-09T21:13:16.000Z', R1F: 0, R2F: 0 },
  { date: '2026-01-09T21:08:16.000Z', R1F: 0, R2F: 0 },
  { date: '2026-01-09T21:03:15.000Z', R1F: 0, R2F: 0 },
  { date: '2026-01-09T20:58:15.000Z', R1F: 0, R2F: 0 },
  { date: '2026-01-09T20:53:14.000Z', R1F: 0, R2F: 0 },
  { date: '2026-01-09T20:48:14.000Z', R1F: 0, R2F: 0 },
  { date: '2026-01-09T20:43:13.000Z', R1F: 0, R2F: 0 },
  { date: '2026-01-09T20:38:13.000Z', R1F: 0, R2F: 0 },
  { date: '2026-01-09T20:33:12.000Z', R1F: 0, R2F: 0 },
  { date: '2026-01-09T20:28:12.000Z', R1F: 0, R2F: 0 },
  { date: '2026-01-09T20:23:11.000Z', R1F: 0, R2F: 0 },
  { date: '2026-01-09T20:18:11.000Z', R1F: 0, R2F: 0 },
  { date: '2026-01-09T20:15:02.000Z', R1F: 0, R2F: 0 },
  { date: '2026-01-09T20:15:01.000Z', R1F: 0, R2F: 0 },
  { date: '2026-01-09T20:13:10.000Z', R1F: 12.65, R2F: 12.64 },
  { date: '2026-01-09T20:08:10.000Z', R1F: 12.64, R2F: 12.68 },
  { date: '2026-01-09T20:03:09.000Z', R1F: 12.64, R2F: 12.65 },
  { date: '2026-01-09T19:58:09.000Z', R1F: 12.64, R2F: 12.64 },
  { date: '2026-01-09T19:53:08.000Z', R1F: 12.68, R2F: 12.68 },
  { date: '2026-01-09T19:48:08.000Z', R1F: 12.65, R2F: 12.65 },
  { date: '2026-01-09T19:43:07.000Z', R1F: 12.64, R2F: 12.65 },
  { date: '2026-01-09T19:38:07.000Z', R1F: 12.66, R2F: 12.68 },
  { date: '2026-01-09T19:33:06.000Z', R1F: 12.63, R2F: 12.64 },
  { date: '2026-01-09T19:28:06.000Z', R1F: 12.64, R2F: 12.65 },
]

const FeedbackThreshold = (props) => {
  const defaultSettings = { settings: { ...DEFAULT_SETTINGS } }
  const {
    device: {
      application_settings: applicationSettingsRaw = defaultSettings,
      application_settings_new: applicationSettingsNewRaw = defaultSettings,
      physical,
    } = {},
    intl: {
      formatMessage,
    },
  } = props

  // Some devices/backends can provide `application_settings(_new)` without a `settings`
  // object (or as null). Normalize to a consistent shape to prevent runtime crashes.
  const applicationSettings = (applicationSettingsRaw && typeof applicationSettingsRaw === 'object')
    ? applicationSettingsRaw
    : defaultSettings

  const applicationSettingsNew = (applicationSettingsNewRaw && typeof applicationSettingsNewRaw === 'object')
    ? applicationSettingsNewRaw
    : defaultSettings

  const settings = {
    ...DEFAULT_SETTINGS,
    ...(applicationSettings.settings && typeof applicationSettings.settings === 'object' ? applicationSettings.settings : {}),
  }

  const settingsNew = {
    ...DEFAULT_SETTINGS,
    ...(applicationSettingsNew.settings && typeof applicationSettingsNew.settings === 'object' ? applicationSettingsNew.settings : {}),
  }

  const deviceCalibrateSettings = []
  const { device } = props
  const hasSettings = !!applicationSettings.settings
  const hasSettingsNew = !!applicationSettingsNew.settings

  let multiplier1 = 1
  let valueKey1 = ''
  let multiplier2 = 1
  let valueKey2 = ''
  let formula1 = ''
  let formula2 = ''
  let reading1New = {}
  let reading1 = {}

  if (physical.deviceType === 'pump') {
    formula1 = 'current4To20'
    formula2 = 'current4To20'
    valueKey1 = 'R1F'
    valueKey2 = 'R2F'
    multiplier1 = 1
    multiplier2 = 1
    reading1New = { R1F: settingsNew.calibrate1, R2F: settingsNew.calibrate2 }
    reading1 = { R1F: settings.calibrate1, R2F: settings.calibrate2 }
  } else if (physical.deviceType === 'valve') {
    formula1 = 'fourToTwentySD1'
    formula2 = 'fourToTwentySD2'
    valueKey1 = '142'
    valueKey2 = '143'
    multiplier1 = 10000
    multiplier2 = 10000
    reading1New = { 142: settingsNew.calibrate1, 143: settingsNew.calibrate2 }
    reading1 = { 142: settings.calibrate1, 143: settings.calibrate2 }
  }

  let firstCalibrate1 = 7
  if (hasSettingsNew && isNumber(settingsNew.calibrate1) && applicationSettingsNew.status === 'new') {
    firstCalibrate1 = pressure(formula1, multiplier1, valueKey1, reading1New, physical)
  } else if (hasSettings && isNumber(settings.calibrate1)) {
    firstCalibrate1 = pressure(formula1, multiplier1, valueKey1, reading1, physical)
  }
  let firstCalibrate2 = 7
  if (hasSettingsNew && isNumber(settingsNew.calibrate2) && applicationSettingsNew.status === 'new') {
    firstCalibrate2 = pressure(formula2, multiplier2, valueKey2, reading1New, physical)
  } else if (hasSettings && isNumber(settings.calibrate2)) {
    firstCalibrate2 = pressure(formula2, multiplier2, valueKey2, reading1, physical)
  }

  deviceCalibrateSettings.push({
    address: device.address,
    status: 'new',
    settings: {
      calibrate1: firstCalibrate1,
      calibrate2: firstCalibrate2,
    },
  })

  const [state, setState] = useState({
    calibrate1Mult: firstCalibrate1,
    calibrate2Mult: firstCalibrate2,
    calibrate1Invalid: false,
    calibrate2Invalid: false,
    disableCalibration: false,
    inputErrorCalibrate1: false,
    inputErrorCalibrate2: false,
  })

  const intervalRef = useRef(null)
  const initialDateBeginRef = useRef(moment.utc().add(-2, 'hours'))
  const initialDateEndRef = useRef(moment.utc())
  const deviceAddressRef = useRef(device.address)
  const timeBegRef = useRef(null)
  const timeEndRef = useRef(null)

  const dateRangePicker = useDateRangePicker(initialDateBeginRef.current, initialDateEndRef.current)
  const {
    state: {
      dateBegin,
      dateEnd,
      dateType,
      dayPickerIsVisible,
      draftRange,
    },
    closeDayPicker,
    editDateRange,
    handleDayClick,
    applyDraftRange,
    resetToLast31Days,
  } = dateRangePicker

  const getNewData = useCallback(() => {
    const timeBeg = timeBegRef.current || (dateBegin ? dateBegin.toISOString() : moment.utc().add(-2, 'hours').toISOString())
    const timeEnd = timeEndRef.current || (dateEnd ? dateEnd.toISOString() : moment.utc().toISOString())

    AppDispatcher.dispatch({
      action: 'get-device-readings',
      timeBeg,
      timeEnd,
      deviceID: deviceAddressRef.current,
      convert: false,
    })
  }, [dateBegin, dateEnd])

  const appDispatch = useCallback(() => {
    const {
      calibrate1Mult,
      calibrate2Mult,
      inputErrorCalibrate1,
      inputErrorCalibrate2,

    } = state

    const { address, physical } = device
    const { setValidity } = props

    let finalCalibrate1 = pressureToCurrent(physical.deviceType === 'pump' ? 'R1F' : '142', calibrate1Mult, physical)
    let finalCalibrate2 = pressureToCurrent(physical.deviceType === 'pump' ? 'R2F' : '143', calibrate2Mult, physical)

    if (physical.deviceType === 'valve') {
      finalCalibrate1 = Math.round(finalCalibrate1)
      finalCalibrate2 = Math.round(finalCalibrate2)
    }

    if (inputErrorCalibrate1 || inputErrorCalibrate2) {
      setValidity(false)
    } else {
      setValidity(true)
    }

    const settings = {
      address,
      status: 'new',
      settings: {
        calibrate1: finalCalibrate1,
        calibrate2: finalCalibrate2,
      },
    }

    if (physical.deviceType === 'pump') {
      const physicals = {
        r1FLow: finalCalibrate1,
        r2FLow: finalCalibrate2,
      }
      AppDispatcher.dispatch({
        action: 'change-physical',
        setting: physicals,
      })
    }

    AppDispatcher.dispatch({
      action: 'change-setting',
      setting: settings,
    })
  }, [
    device.address,
    device.physical,
    props.setValidity,
    state.calibrate1Mult,
    state.calibrate2Mult,
    state.inputErrorCalibrate1,
    state.inputErrorCalibrate2,
  ])

  useEffect(() => {
    deviceAddressRef.current = device.address
  }, [device.address])

  useEffect(() => {
    // Keep latest range in refs for the polling interval and fetch immediately.
    timeBegRef.current = dateBegin ? dateBegin.toISOString() : moment.utc().add(-2, 'hours').toISOString()
    timeEndRef.current = dateEnd ? dateEnd.toISOString() : moment.utc().toISOString()
    getNewData()
  }, [dateBegin, dateEnd, getNewData])

  useEffect(() => {
    // Poll using refs (doesn't depend on render timing).
    intervalRef.current = setInterval(() => {
      getNewData()
    }, 60000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [getNewData])

  useEffect(() => {
    // Only push settings when calibration inputs change.
    appDispatch()
  }, [appDispatch])

  const childChange = useCallback((event) => {
    const { target: { name, value } } = event
    let newValue = value

    switch (name) {
      case 'calibrate1Mult':
        newValue = value
        break

      case 'calibrate2Mult':
        newValue = value
        break

      default:
        return
    }

    let newState = updateRA(state, {
      [name]: { $set: newValue },
    })

    if (name === 'calibrate1Mult') {
      if (!isNumber(value)) {
        newState = updateRA(newState, {
          inputErrorCalibrate1: { $set: true },
        })
      } else {
        newState = updateRA(newState, {
          inputErrorCalibrate1: { $set: false },
        })
      }
    }

    if (name === 'calibrate2Mult') {
      if (!isNumber(value)) {
        newState = updateRA(newState, {
          inputErrorCalibrate2: { $set: true },
        })
      } else {
        newState = updateRA(newState, {
          inputErrorCalibrate2: { $set: false },
        })
      }
    }

    // Let effects react to state changes (prevents tight loops / flicker).
    setState(newState)
  }, [state])

  const {
    calibrate1Mult,
    calibrate2Mult,
    disableCalibration,
    inputErrorCalibrate2,
  } = state

  const {
    deviceReading: deviceReadingProp,
    query,
  } = props

  // Use mock data if flag is enabled, otherwise use real data
  const deviceReading = USE_MOCK_DATA ? MOCK_DEVICE_READINGS : (deviceReadingProp || [])

  const input = `${query?.input ?? '1'}`
  const deviceType = query?.deviceType

  return (
    <div>
      <div>
        <div className={`${styles.inputArea} ${styles.paddedInputArea}`}>
          <section className={styles.heading}>
            <h3>
              <FormattedMessage
                id='setting.pump.custom.calibration.label'
                defaultMessage='Feedback Threshold'
                description='Feedback Threshold label'
              />
            </h3>
            <h5 className={`${styles.windMachineCalibrationWarning}`}>
              <FormattedMessage
                id='setting.pump.warning.threshold'
                defaultMessage='WARNING: Customizing feedback threshold should only be done in circumstances when the Feedback of your irrigation system is above the default threshold when the pump is OFF.'
                description='Warning threshold label'
              />
            </h5>
          </section>
          <br />
          <section className={styles.heading}>
            <h5>
              <FormattedMessage
                id='setting.pump.feedbackThreshold.text.label'
                defaultMessage='To determine a custom feedback threshold:'
                description='Customize Calibration Text label'
              />
            </h5>
            <h5>
              <FormattedMessage
                id='setting.pump.feedbackThreshold.stepOne.label'
                defaultMessage='(1) Determine the highest pressure when the device is OFF.'
                description='Customize Calibration Text label'
              />
            </h5>
            <h5>
              <FormattedMessage
                id='setting.pump.feedbackThreshold.stepTwo.label'
                defaultMessage='(2) Determine the lowest pressure when the device is ON.'
                description='Customize Calibration Text label'
              />
            </h5>
            <h5>
              <FormattedMessage
                id='setting.pump.feedbackThreshold.stepThree.label'
                defaultMessage='(3) Set "Feedback Threshold" to a pressure value between the two values.'
                description='Customize Calibration Text label'
              />
            </h5>
          </section>
          <br />
          <section>
            <h5>
              <FormattedMessage
                id='setting.pump.feedbackThreshold.feedback'
                defaultMessage='Feedback Threshold: '
                description='Feedback Threshold label'
              />
            </h5>
            <div className={`${styles.controlNumber} ${inputErrorCalibrate2 && (styles.inputIssue)}`}>
              <DecimalInput
                value={input === '1' ? calibrate1Mult : calibrate2Mult}
                childChange={childChange}
                field={input === '1' ? 'calibrate1Mult' : 'calibrate2Mult'}
                style={styles.controlNumberInput}
              />
            </div>
          </section>
          <br />
          <br />
          <div className={stylesTele.dateInputs} style={{ marginBottom: '25px' }}>
            <DateRangePicker
              dateBegin={dateBegin}
              dateEnd={dateEnd}
              dateType={dateType}
              dayPickerIsVisible={dayPickerIsVisible}
              draftRange={draftRange}
              onClose={closeDayPicker}
              onEditRange={editDateRange}
              onDayClick={handleDayClick}
              onApplyRange={applyDraftRange}
              onResetToLast31Days={resetToLast31Days}
              layout='both'
              buttonStyle={{ marginLeft: 0 }}
            />
          </div>
          <Graph
            deviceReading={deviceReading}
            calibrate1={calibrate1Mult}
            calibrate2={calibrate2Mult}
            disableCalibration={disableCalibration}
            formatMessage={formatMessage}
            physical={physical}
            input={input}
            deviceType={deviceType}
          />
        </div>
      </div>
    </div>

  )
}

FeedbackThreshold.propTypes = {
  device: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
}

export default injectIntl(FeedbackThreshold)
